import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
  type DialogProps as AriaDialogProps,
  type ModalOverlayProps as AriaModalOverlayProps,
} from 'react-aria-components';

import { cx } from '../../../utils/cx';
import CloseButton from '../../base/buttons/CloseButton';

const ModalOverlay = (props: AriaModalOverlayProps) => {
  return (
    <AriaModalOverlay
      {...props}
      className={(state) =>
        cx(
          'bg-overlay/70 outline-hidden fixed inset-0 z-50 flex min-h-dvh w-full items-end justify-center overflow-y-auto px-4 pb-[clamp(16px,8vh,64px)] pt-4 backdrop-blur-[6px] sm:items-center sm:justify-center sm:p-8',
          state.isEntering && 'animate-in fade-in duration-300 ease-out',
          state.isExiting && 'animate-out fade-out duration-200 ease-in',
          typeof props.className === 'function' ? props.className(state) : props.className
        )
      }
    />
  );
};

const Modal = (props: AriaModalOverlayProps) => (
  <AriaModal
    {...props}
    className={(state) =>
      cx(
        'outline-hidden max-h-full w-full align-middle max-sm:overflow-y-auto max-sm:rounded-xl',
        state.isEntering && 'animate-in zoom-in-95 duration-300 ease-out',
        state.isExiting && 'animate-out zoom-out-95 duration-200 ease-in',
        typeof props.className === 'function' ? props.className(state) : props.className
      )
    }
  />
);

const Dialog = (props: AriaDialogProps) => (
  <AriaDialog {...props} className={cx('outline-hidden flex w-full items-center justify-center', props.className)} />
);

const modalSizes = {
  xs: 'w-xs',
  sm: 'w-sm',
  md: 'w-md',
  lg: 'w-lg',
  xl: 'w-xl',
} as const;

interface IModalProps extends React.PropsWithChildren {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;
  size?: keyof typeof modalSizes;
  /** Optional class for the overlay (e.g. to position the modal vertically). */
  overlayClassName?: string;
}

const ModalComponent = ({ children, className, isOpen, onOpenChange, size = 'md', overlayClassName }: IModalProps) => {
  const overlayProps: AriaModalOverlayProps = {
    isDismissable: true,
    isKeyboardDismissDisabled: false,
    ...(overlayClassName != null && { className: overlayClassName }),
  };

  const diaglogTriggerProps = {
    isOpen,
    onOpenChange,
  };

  const sizeClass = modalSizes[size];

  return (
    <AriaDialogTrigger {...diaglogTriggerProps}>
      <ModalOverlay {...overlayProps}>
        <Modal>
          <Dialog>
            <div
              className={cx('bg-primary flex flex-col items-center justify-center rounded-2xl', sizeClass, className)}
            >
              {children}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </AriaDialogTrigger>
  );
};

interface IHeaderProps extends React.PropsWithChildren {
  className?: string;
  onClose?: () => void;
}

const Header = ({ className, children, onClose }: IHeaderProps) => {
  return (
    <div
      className={cx(
        'relative flex w-full justify-between px-6 pb-5 pt-6 text-lg font-semibold',
        onClose ? 'items-center' : 'items-start',
        className
      )}
    >
      {children}
      {onClose && <CloseButton className="absolute right-3 top-3" onClick={onClose} size="lg" />}
    </div>
  );
};

interface IBodyProps extends React.PropsWithChildren {
  className?: string;
}

const Body = ({ className, children }: IBodyProps) => {
  return <div className={cx('flex w-full flex-col items-center p-4', className)}>{children}</div>;
};

export default Object.assign(ModalComponent, {
  Header,
  Body,
});
